import { useCallback, useEffect, useRef, useState } from "react";
import { UploadCloud, Loader2, FileText, ExternalLink, Trash2, FolderOpen, Image as ImageIcon, FileSpreadsheet, Mail, Wrench, Briefcase, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const slug = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "");
const ICON = { immagine: ImageIcon, excel: FileSpreadsheet, pdf: FileText, documento: FileText };
const SOURCE_LABEL = { email: "da email", upload: "caricato", tool_output: "generato da tool", tool_input: "input tool", tool_template: "template tool", journal: "giornale" };

export const DocumentsPanel = ({ siteId, filterCategory }) => {
  const [data, setData] = useState({ files: [], categories: [], unclassified: 0 });
  const [cat, setCat] = useState(filterCategory || "");
  const [uploading, setUploading] = useState(false);
  const ref = useRef();
  const load = useCallback(() => api.siteDocuments(siteId).then(setData).catch(() => {}), [siteId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (list) => {
    setUploading(true);
    for (const f of Array.from(list)) {
      try { await api.uploadSiteFile(siteId, f); } catch { toast.error(`Errore caricamento ${f.name}`); }
    }
    setUploading(false);
    await load();
  };
  const del = async (fid) => { await api.deleteFile(fid); load(); };
  const setCategory = async (fid, category) => { await api.setFileCategory(fid, category); load(); };
  const files = data.files.filter((f) => (cat === "" ? true : cat === "__none" ? !f.doc_category : f.doc_category === cat));
  const total = data.files.length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 items-start" data-testid="documents-panel">
      <aside className="bg-white border border-slate-200 rounded-xl p-2 lg:sticky lg:top-4">
        <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Categorie</div>
        <nav className="space-y-0.5">
          <button onClick={() => setCat("")} data-testid="doc-cat-all" className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${cat === "" ? "bg-orange-50 text-orange-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
            <span className="inline-flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Tutti</span><span className="font-mono text-xs">{total}</span>
          </button>
          {data.categories.map((c) => (
            <button key={c.name} onClick={() => setCat(c.name)} data-testid={`doc-cat-${slug(c.name)}`} disabled={c.count === 0}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-left ${cat === c.name ? "bg-orange-50 text-orange-700 font-semibold" : c.count === 0 ? "text-slate-300" : "text-slate-700 hover:bg-slate-50"}`}>
              <span className="truncate">{c.name}</span><span className="font-mono text-xs">{c.count}</span>
            </button>
          ))}
          {data.unclassified > 0 && (
            <button onClick={() => setCat("__none")} data-testid="doc-cat-unclassified" className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${cat === "__none" ? "bg-orange-50 text-orange-700 font-semibold" : "text-amber-700 hover:bg-amber-50"}`}>
              <span className="inline-flex items-center gap-2"><HelpCircle className="w-4 h-4" /> Da classificare</span><span className="font-mono text-xs">{data.unclassified}</span>
            </button>
          )}
        </nav>
      </aside>

      <div className="space-y-3 min-w-0">
        <div onClick={() => ref.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }} data-testid="site-file-dropzone"
          className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-slate-400 bg-white rounded-xl p-5 text-center">
          <input ref={ref} type="file" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="site-file-input" />
          {uploading ? <Loader2 className="w-6 h-6 text-orange-500 mx-auto animate-spin" /> : <UploadCloud className="w-6 h-6 text-slate-400 mx-auto" />}
          <p className="text-sm text-slate-600 mt-1.5 font-medium">Trascina o clicca per archiviare documenti — l'AI li classifica automaticamente</p>
          <p className="text-xs text-slate-400 mt-0.5">Include i documenti delle offerte collegate e gli allegati email assegnati al cantiere.</p>
        </div>
        {cat && <div className="text-xs text-slate-500" data-testid="doc-filter-label">Filtro: <span className="font-semibold text-slate-800">{cat === "__none" ? "Da classificare" : cat}</span> · {files.length} documenti</div>}
        {files.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">Nessun documento in questa categoria.</p> : (
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {files.map((f) => {
              const Icon = ICON[f.category] || FileText;
              return (
                <div key={f.id} data-testid={`site-file-${f.id}`} className="flex items-center gap-3 px-4 py-2.5">
                  <Icon className="w-5 h-5 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{f.doc_label || f.original_filename}</div>
                    <div className="text-xs text-slate-400 truncate">
                      {f.doc_label && <span>{f.original_filename} · </span>}{(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString("it-IT")}
                      {f.origin === "offerta" ? <span className="inline-flex items-center gap-1 ml-1 text-sky-600"><Briefcase className="w-3 h-3" /> offerta {f.offer_title}</span>
                        : f.source === "email" ? <span className="inline-flex items-center gap-1 ml-1"><Mail className="w-3 h-3" /> da email</span>
                        : f.source?.startsWith("tool") ? <span className="inline-flex items-center gap-1 ml-1"><Wrench className="w-3 h-3" /> {SOURCE_LABEL[f.source]}</span>
                        : <span className="ml-1">{SOURCE_LABEL[f.source] || f.source}</span>}
                    </div>
                  </div>
                  <select value={f.doc_category || ""} onChange={(e) => setCategory(f.id, e.target.value)} data-testid={`doc-category-select-${f.id}`} className="text-[11px] border border-slate-200 rounded-lg px-1.5 py-1 bg-white text-slate-600 max-w-[150px]">
                    <option value="" disabled>Categoria…</option>
                    {data.categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                  <a href={api.fileUrl(f.id)} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><ExternalLink className="w-4 h-4" /></a>
                  {f.origin !== "offerta" && <button onClick={() => del(f.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
